import React, { Suspense } from 'react';
// import MainLayout from "../layout";
import WelcomeScreen from '../views/dashboard/WelcomeScreen';
import ThankYouScreen from '../views/dashboard/ThankYouScreen';
import SingleSignOn from 'views/pages/SingleSignOn';
import LogoutScreen from 'views/pages/LogoutScreen';
import { Modal } from '@mui/material';
import Loadable from 'ui-components/route-components/Loadable';
import GiveawayLanding from 'views/pages/Giveaway';
import BackrrApps from 'views/backrr-apps';
import BackrrLoadingAnimation from 'ui-components/route-components/BackrrLoadingAnimation';
import FeaturePopup from 'views/components/FeaturePopup';
import ResourcePopup from 'views/components/ResourcePopup';
import CapTable from 'views/investors/CapTable';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import ThemeBGWrapper from 'ui-components/ThemeBGWrapper';
import { Breadcrumb } from 'rsuite';
import BreadcrumbHeader from 'layout/BreadcrumbsHeader';
import { hasPendingSubscriptionIntent } from 'utilities/subscriptionIntent';
import ProtectedRoute from './ProtectedRoute';
import OwnerOnlyRoute from './OwnerOnlyRoute';
import { profileTabFromPath, profileTabPath } from 'utilities/profileTabs';

// const MainLayout = Loadable(React.lazy(() => import('layout')));
const ViewProfileInformation = Loadable(
  React.lazy(() => import('views/profile/profileSettings'))
);
const Rounds = Loadable(React.lazy(() => import('views/rounds/Rounds')));
const CreateRound = Loadable(
  React.lazy(() => import('views/rounds/CreateRound'))
);
const ViewRound = Loadable(React.lazy(() => import('views/rounds/ViewRound')));
const ActiveRound = Loadable(
  React.lazy(() => import('views/rounds/ActiveRound'))
);
const Reports = Loadable(React.lazy(() => import('views/reports/Reports')));
const CreateReport = Loadable(
  React.lazy(() => import('views/reports/CreateReport'))
);
const UpdateProfile = Loadable(
  React.lazy(() => import('views/profile/profileSettings/updateProfile/index'))
);
const OnboardingLayout = Loadable(
  React.lazy(() => import('layout/MainContent/OnboardingLayout'))
);
// const OnboardingV2 = Loadable(
//   React.lazy(() => import('views/profile/onboarding-v2'))
// );
const FindInvestors = Loadable(
  React.lazy(() => import('views/find-investors'))
);
const Performance = Loadable(React.lazy(() => import('views/performance')));
const EditPerformance = Loadable(
  React.lazy(() => import('views/performance/EditPerformance'))
);
const ViewSettingsInformation = Loadable(
  React.lazy(() => import('views/settings'))
);
const AddUser = Loadable(React.lazy(() => import('views/settings/AddUser')));
const Wrapper = Loadable(React.lazy(() => import('ui-components/Wrapper')));
const ViewInvestors = Loadable(React.lazy(() => import('views/investors')));
const EditInvestor = Loadable(
  React.lazy(() => import('views/investors/EditInvestor'))
);
const ManageSubscription = Loadable(
  React.lazy(() => import('views/Subscription/ManageSubscriptionNew'))
);
const OrdersAndInvoices = Loadable(
  React.lazy(() => import('views/Subscription/OrdersAndInvoices'))
);
const DataRoom = Loadable(React.lazy(() => import('views/data-room')));
const ViewDataRoom = Loadable(
  React.lazy(() => import('views/data-room/ViewDataRoom'))
);
const ResourceDetail = Loadable(
  React.lazy(() => import('views/resources/ResourceDetail'))
);

const BackrrLoadable = (Component) => (props) => {
  return (
    <Suspense fallback={<BackrrLoadingAnimation />}>
      <Component {...props} />
      <FeaturePopup />
      {/* <ResourcePopup /> */}
    </Suspense>
  );
};
const MainLayout = BackrrLoadable(React.lazy(() => import('layout')));

// Permanent redirect for the legacy find-investor(s) path, keeping any
// query string / hash so old links (emails, bookmarks) land on the same view.
const InvestorNetworkRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/investor-network${search}${hash}`} replace />;
};

// Redirect legacy /investors/* routes to /pipeline and /captable
const InvestorsPipelineRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/pipeline${search}${hash}`} replace />;
};

const InvestorsCapTableRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/captable${search}${hash}`} replace />;
};

const InvestorsCapTableAddRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/captable/add-investment${search}${hash}`} replace />;
};

const InvestorsCapTableEditRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={`/captable/edit-investment${search}${hash}`} replace />;
};

// Profile tabs moved from `?page=` to real paths. Anything still pointing at the
// old form — `/`, `/?page=profile_views`, `/profile?page=profile_views` — is
// mapped onto the new path. Other query params are preserved, because
// `?upgrade=true` opens the pricing modal from any route.
const ProfileTabRedirect = () => {
  const { search, hash } = useLocation();
  const params = new URLSearchParams(search);
  const legacyTab = params.get('page');
  params.delete('page');
  const rest = params.toString();
  return (
    <Navigate
      to={`${profileTabPath(legacyTab)}${rest ? `?${rest}` : ''}${hash}`}
      replace
    />
  );
};

// One component serves both /profile and /profile/views, reading the tab off the
// path rather than taking it as a prop. That matters for more than tidiness:
// React Router keeps a matched subtree mounted only while the element type is
// unchanged, so a component per tab would remount the screen on every switch -
// refetching the profile queries and flashing the tab/content skeletons.
// A stray legacy `?page=` still redirects, so the tab in the URL and the tab on
// screen can never disagree.
const ProfileScreen = () => {
  const { pathname, search } = useLocation();
  if (new URLSearchParams(search).get('page') !== null) {
    return <ProfileTabRedirect />;
  }
  return <ViewProfileInformation tabValue={profileTabFromPath(pathname)} />;
};

const OnboardedRoute = () => {
  const navigate = useNavigate();

  navigate(hasPendingSubscriptionIntent() ? '/?upgrade=true' : '/profile', {replace: true});
  return <ThemeBGWrapper>
    <BreadcrumbHeader />
    <BackrrLoadingAnimation />
  </ThemeBGWrapper>
};

const MainRoutes = {
  path: '',
  // element: <> />,
  children: [
    {
      path: '',
      element: <MainLayout />,
      children: [
        {
          path: '/',
          children: [
            {
              path: '',
              element: <ProfileTabRedirect />,
            },
            {
              path: 'profile-update',
              element: <ProtectedRoute Component={UpdateProfile} module="profile" action="edit" />,
            },
          ],
        },
        {
          path: 'profile',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={ProfileScreen} module="profile" action="view" />,
            },
            {
              path: 'views',
              element: <ProtectedRoute Component={ProfileScreen} module="profile" action="profile_views" />,
            },
          ],
        },
        {
      path: 'onboarded',
      element: <OnboardedRoute />,
    },
    {
      path: 'login',
      element: <OnboardedRoute />,
    },
        {
          path: 'active-round',
          element: <ProtectedRoute Component={ActiveRound} module="funding_rounds" action="view" />,
        },
        {
          path: 'rounds',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={Rounds} module="funding_rounds" action="view" />,
            },
            {
              path: 'create_round',
              element: <ProtectedRoute Component={CreateRound} module="funding_rounds" action="add" />,
            },
            {
              path: 'edit_round',
              element: <ProtectedRoute Component={CreateRound} module="funding_rounds" action="edit" />,
            },
            {
              path: 'detail',
              element: <ProtectedRoute Component={() => <ViewRound tabValue="view_details" />} module="funding_rounds" action="view" />,
            },
            {
              path: 'investors',
              element: <ProtectedRoute Component={() => <ViewRound tabValue="round_investors" />} module="funding_rounds" action="view" />,
            },
            {
              path: 'documents',
              element: <ProtectedRoute Component={() => <ViewRound tabValue="documents" />} module="funding_rounds" action="view" />,
            },
          ],
        },
        {
          path: 'reports',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={Reports} module="reports" action="view" />,
            },
            {
              path: 'create_report',
              element: <ProtectedRoute Component={CreateReport} module="reports" action="add" />,
              // children: [
              //   {
              //     path: ""
              //   },
              // {
              //   path: ":id"
              //   // element: <CreateReport />
              // }
              // ]
            },
            {
              path: 'edit_report',
              element: <ProtectedRoute Component={CreateReport} module="reports" action="edit" />,
            },
            // ,
            // {
            //     path: 'view/:id',
            //     element: <ViewRound />
            // },
          ],
        },
        {
          path: 'performance',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={Performance} module="performance" action="view" />,
            },
            {
              path: 'add_performance',
              element: <ProtectedRoute Component={EditPerformance} module="performance" action="add_data" />,
            },
            {
              path: 'edit_performance',
              element: <ProtectedRoute Component={EditPerformance} module="performance" action="edit" />,
            },
          ],
        },
        {
          path: 'investor-network',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={FindInvestors} module="investor_network" action="view" />,
            },
          ],
        },
        {
          path: 'captable',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={() => <ViewInvestors tabValue="invested" />} module="captable" action="view" />,
            },
            {
              path: 'add-investment',
              element: <ProtectedRoute Component={EditInvestor} module="captable" action="add" />,
            },
            {
              path: 'edit-investment',
              element: <ProtectedRoute Component={EditInvestor} module="captable" action="edit" />,
            },
          ],
        },
        {
          path: 'find-investor',
          element: <InvestorNetworkRedirect />,
        },
        {
          path: 'find-investor/*',
          element: <InvestorNetworkRedirect />,
        },
        {
          path: 'find-investors',
          element: <InvestorNetworkRedirect />,
        },
        {
          path: 'find-investors/*',
          element: <InvestorNetworkRedirect />,
        },
        {
          path: 'settings',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={ViewSettingsInformation} module="settings" action="manage_users" />,
            },
            {
              path: 'add_user',
              element: <ProtectedRoute Component={AddUser} module="settings" action="manage_users" />,
            },
          ],
        },
        {
          path: 'manage_subscription',
          children: [
            {
              path: '',
              element: <OwnerOnlyRoute Component={ManageSubscription} />,
            },
          ],
        },
        {
          path: 'orders-invoices',
          children: [
            {
              path: '',
              element: <OwnerOnlyRoute Component={OrdersAndInvoices} />,
            },
            {
              path: 'subscriptions',
              element: <OwnerOnlyRoute Component={() => <OrdersAndInvoices value={'subscriptions'} />} />,
            },
            {
              path: 'one-time',
              element: <OwnerOnlyRoute Component={() => <OrdersAndInvoices value={'one-time'} />} />,
            },
          ],
        },
        // {
        //   path: 'drive',
        //   children: [
        //     { path: '', element: <DataRoom /> },
        //     { path: 'documents', element: <DataRoom tabValue={"documents"} /> },
        //     { path: 'data-rooms', element: <DataRoom tabValue={"data-rooms"} /> },
        //     { path: 'data-rooms/detail', element: <ViewDataRoom tabValue="detail" /> },
        //     // { path: 'add_user', element: <DataRoom /> },
        //   ],
        // },
        { path: 'documents', element: <ProtectedRoute Component={() => <DataRoom tabValue={'documents'} />} module="drive" action="documents" /> },
        { path: 'data-rooms', element: <ProtectedRoute Component={() => <DataRoom tabValue={'data-rooms'} />} module="drive" action="data_rooms" /> },
        {
          path: 'data-rooms/detail',
          element: <ProtectedRoute Component={() => <ViewDataRoom tabValue="detail" />} module="drive" action="data_rooms" />,
        },
        // { path: 'add_user', element: <DataRoom /> },

        {
          path: 'investors',
          children: [
            {
              path: '',
              element: <InvestorsPipelineRedirect />,
            },
            {
              path: 'pipeline',
              element: <InvestorsPipelineRedirect />,
            },
            {
              path: 'invested',
              element: <InvestorsCapTableRedirect />,
            },
            {
              path: 'invested/add-investment',
              element: <InvestorsCapTableAddRedirect />,
            },
            {
              path: 'invested/edit-investment',
              element: <InvestorsCapTableEditRedirect />,
            },
            {
              path: '*',
              element: <InvestorsPipelineRedirect />,
            },
          ],
        },
        {
          path: 'pipeline',
          element: <ProtectedRoute Component={() => <ViewInvestors tabValue="pipeline" />} module="pipeline" action="view" />,
        },
        {
          path: 'tools',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={BackrrApps} module="ai_insights" action="view" />,
            },
          ],
        },
        {
          path: 'resources',
          children: [
            {
              path: '',
              element: <ProtectedRoute Component={() => <BackrrApps tabValue={"resources"} />} module="resources" action="view" />,
            },
            {
              path: ':resourceId',
              element: <ProtectedRoute Component={ResourceDetail} module="resources" action="view" />,
            },
          ],
        },
        {
          path: '*',
          element: <ProfileTabRedirect />,
        },
      ],
    },
    {
      path: '/welcome',
      element: (
        <OnboardingLayout>
          {' '}
          <WelcomeScreen />
        </OnboardingLayout>
      ),
    },
    {
      path: '/thank-you',
      element: (
        <OnboardingLayout>
          {' '}
          <ThankYouScreen />
        </OnboardingLayout>
      ),
    },
    {
      path: '/logout',
      element: <LogoutScreen />,
    },
    {
      path: '/cHJpdmF0ZS1yb3V0ZQ==',
      element: <SingleSignOn />,
    },
    {
      path: 'fundraising-fastforward-giveaway',
      element: (
        <>
          <GiveawayLanding />
        </>
      ),
    },
  ],
};

export default MainRoutes;
